import { Inject, Injectable } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import dayjs from 'dayjs';
import * as FlatModels from '@medsurf/flat-models';
import { FLAT_SETTINGS, type SettingControlModel } from '../../internal';

@Injectable({
  providedIn: 'root',
})
export class MediaControlService {
  constructor(@Inject(FLAT_SETTINGS) private settings: SettingControlModel,
              private httpClient: HttpClient) {
  }

  getUrl(media: FlatModels.MediaEntityModels.MediaEntityType,
         fileType: 'file' | 'originalFile' | 'thumbnail' | 'tileFolder' | 'poster' = 'file',
         forceApi = false): string {
    if (!media) return '';

    let url: string | null = media.file;
    if (fileType === 'poster') {
      url = 'poster' in media && media.poster ? media.poster : null
    } else if (fileType === 'tileFolder') {
      url = 'tileFolder' in media && media.tileFolder ? media.tileFolder : null
    } else {
      url = media[fileType] ?? null;
    }


    let hash = '';
    if (fileType !== 'tileFolder') {
      if (media.checksum) {
        hash = '?' + media.checksum;
        hash += '&' + new Date(media.editedAt as Date).getTime();
      }
      if (media.type === 'MediaVideo' && (fileType === 'poster' || fileType === 'thumbnail')) {
        hash = '?' + new Date(media.editedAt as Date).getTime();
      }
    }
    if (!url) return '';
    if (!this.settings.s3endpointUrl || !this.settings.endpointUrl || !this.settings.s3Bucket) return '';

    return (media.access === FlatModels.MediaEntityModels.MediaAccess.enum.private || forceApi) && fileType !== 'tileFolder' ?
      this.settings.endpointUrl + FlatModels.ApiControlModels.ApiFilePath.private + '/' + url + hash :
      this.settings.s3endpointUrl + this.settings.s3Bucket + '/' + url + hash;
  }

  getPDFDownloadUrl(media: FlatModels.MediaEntityModels.MediaEntityType, type: 'author' | 'viewer') {
    const formattedDate = dayjs (media.editedAt).format('DD.MM.YYYY');
    const name = `${media.name}_${type}_${formattedDate}.pdf`;
    return this.getDownloadUrl(media, false, name);
  }

  getDownloadUrl(media: FlatModels.MediaEntityModels.MediaEntityType, originalFile = false, name?: string) {
    const query = new URLSearchParams();
    if (originalFile) query.set('originalFile', '');
    if (name) query.set('name', name);
    const queryString = query.toString();
    return `${this.settings.endpointUrl}${FlatModels.ApiControlModels.ApiFilePath.download}/${media.id}${queryString ? '?' + queryString : ''}`
  }
  
  getEditorUrl(media: FlatModels.MediaEntityModels.MediaEntityType) {
    if (media) {
      return this.settings.endpointUrl + 'v1/id2url/' + media.id;
    }
    return '';
  }

  isVideo(media: FlatModels.MediaEntityModels.MediaEntityType): media is FlatModels.MediaEntityModels.MediaVideo {
    return media?.type === FlatModels.MediaEntityModels.MediaType.enum.MediaVideo;
  }

  isAudio(media: FlatModels.MediaEntityModels.MediaEntityType): media is FlatModels.MediaEntityModels.MediaAudio {
    return media?.type === FlatModels.MediaEntityModels.MediaType.enum.MediaAudio;
  }

  isDocument(media: FlatModels.MediaEntityModels.MediaEntityType): media is FlatModels.MediaEntityModels.MediaDocument {
    return media?.type === FlatModels.MediaEntityModels.MediaType.enum.MediaDocument;
  }

  isDeepzoom(media: FlatModels.MediaEntityModels.MediaEntityType): media is FlatModels.MediaEntityModels.MediaDeepzoom {
    return media?.type === FlatModels.MediaEntityModels.MediaType.enum.MediaDeepzoom;
  }

  isImage(media: FlatModels.MediaEntityModels.MediaEntityType): media is FlatModels.MediaEntityModels.MediaImage {
    return media?.type === FlatModels.MediaEntityModels.MediaType.enum.MediaImage;
  }

  getMediaTypeIcon(media: FlatModels.MediaEntityModels.MediaEntityType): string {
    return this.getMediaTypeIconByType(media?.type);
  }
  
  getMediaTypeIconByType(type: FlatModels.MediaEntityModels.MediaType) {
    switch (type) {
      case FlatModels.MediaEntityModels.MediaType.enum.MediaDocument:
        return FlatModels.ButtonDesignModels.ButtonDefaultIcons.MEDIA_DOCUMENT;
      case FlatModels.MediaEntityModels.MediaType.enum.MediaAudio:
        return FlatModels.ButtonDesignModels.ButtonDefaultIcons.MEDIA_AUDIO;
      case FlatModels.MediaEntityModels.MediaType.enum.MediaImage:
        return FlatModels.ButtonDesignModels.ButtonDefaultIcons.MEDIA_IMAGE;
      case FlatModels.MediaEntityModels.MediaType.enum.MediaDeepzoom:
        return FlatModels.ButtonDesignModels.ButtonDefaultIcons.MEDIA_DEEPZOOM;
      case FlatModels.MediaEntityModels.MediaType.enum.MediaVideo:
        return FlatModels.ButtonDesignModels.ButtonDefaultIcons.MEDIA_VIDEO;
      default:
        return FlatModels.ButtonDesignModels.ButtonDefaultIcons.MEDIA_DEFAULT;
    }
  }

  getMediaTypeLabel(media: FlatModels.MediaEntityModels.MediaEntityType): string {
    return this.getMediaTypeLabelByType(media?.type);
  }
  
  getMediaTypeLabelByType(type: FlatModels.MediaEntityModels.MediaType) {
    switch (type) {
      case FlatModels.MediaEntityModels.MediaType.enum.MediaDocument:
        return 'file';
      case FlatModels.MediaEntityModels.MediaType.enum.MediaAudio:
        return 'audio';
      case FlatModels.MediaEntityModels.MediaType.enum.MediaImage:
        return 'image';
      case FlatModels.MediaEntityModels.MediaType.enum.MediaDeepzoom:
        return 'deepzoom';
      case FlatModels.MediaEntityModels.MediaType.enum.MediaVideo:
        return 'video';
      default:
        return 'text';
    }
  }

  async downloadFile(url: string, name: string) {
    const res = await fetch(url);
    const link = document.createElement('a');
    if (res.ok) {
      const blob = await res.blob();
      link.href = URL.createObjectURL(blob);
    } else {
      // Fallback to original link
      link.href = url;
    }
    link.target = '_blank';
    link.download = name;
    link.click();
  }
}